import { useCallback, useEffect, useState } from 'react';
import { ELECTRIC_TOLLS } from './invoiceV2';

const EMPTY = { status: 'idle', groups: [], unsupportedCount: 0, error: '' };

export function groupInvoiceTariffs(data) {
    if (data?.schema_version !== '2.0' || !Array.isArray(data.tarifas)) {
        throw new Error('El catálogo de Neuro no tiene un formato válido. Reintenta la consulta.');
    }
    const ids = new Set();
    for (const tariff of data.tarifas) {
        if (!tariff || typeof tariff.tarifa_id !== 'string' || !tariff.tarifa_id.trim()
            || typeof tariff.calculable !== 'boolean' || ids.has(tariff.tarifa_id)
            || ['nombre', 'tarifa_atr', 'motivo'].some(key => tariff[key] != null && typeof tariff[key] !== 'string')) {
            throw new Error('El catálogo de Neuro no tiene un formato válido. Reintenta la consulta.');
        }
        ids.add(tariff.tarifa_id);
    }
    // This list describes the motor's supported inputs, never a fallback catalog.
    const groups = ELECTRIC_TOLLS.map(toll => ({ toll, tariffs: data.tarifas.filter(tariff => tariff.tarifa_atr === toll) }))
        .filter(group => group.tariffs.length > 0);
    return { groups, unsupportedCount: data.tarifas.filter(tariff => !ELECTRIC_TOLLS.includes(tariff.tarifa_atr)).length };
}

export default function useInvoiceTariffCatalog(api, active, geoZone = 'PENINSULA') {
    const [state, setState] = useState(EMPTY);
    const [revision, setRevision] = useState(0);
    const retry = useCallback(() => {
        setState({ ...EMPTY, owner: api, zone: geoZone, status: 'loading' });
        setRevision(value => value + 1);
    }, [api, geoZone]);

    useEffect(() => {
        if (!active) { setState(EMPTY); return undefined; }
        let current = true;
        const controller = new AbortController();
        setState({ ...EMPTY, owner: api, zone: geoZone, status: 'loading' });
        api(`/tariffs?geo_zone=${encodeURIComponent(geoZone)}`, { signal: controller.signal }).then(data => {
            const grouped = groupInvoiceTariffs(data);
            if (current) setState({ ...EMPTY, ...grouped, owner: api, zone: geoZone, status: 'ready' });
        }).catch(failure => {
            if (current) setState({ ...EMPTY, owner: api, zone: geoZone, status: 'error', error: failure.message || 'No he podido consultar las tarifas de Neuro.' });
        });
        return () => { current = false; controller.abort(); };
    }, [api, active, revision, geoZone]);

    // An earlier user's result must never become the next session's options.
    const result = active && state.owner === api && state.zone === geoZone ? state : EMPTY;
    return { ...result, loading: active && ['idle', 'loading'].includes(result.status), ready: result.status === 'ready', retry };
}
